import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Loader2, Tag, Check, X, FileText, Lightbulb, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { validateCoupon, startCheckout, getPricing } from "@/lib/api";

const gbp = (n) => `£${Number(n).toFixed(2)}`;

const useCountdown = (endsAt) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) {
      setLeft(null);
      return;
    }
    const tick = () => {
      const ms = new Date(endsAt).getTime() - Date.now();
      setLeft(ms > 0 ? ms : 0);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  if (left == null) return null;
  const s = Math.floor(left / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export const Paywall = ({ jobId, onUnlocked }) => {
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null); // { discount_percent, code, final_price }
  const [checking, setChecking] = useState(false);
  const [paying, setPaying] = useState(false);
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    getPricing().then(setPricing).catch(() => setPricing({ base_price: 15, sale_active: false, price: 15 }));
  }, []);

  const countdown = useCountdown(pricing?.sale_active ? pricing?.sale_ends_at : null);

  const effective = pricing ? pricing.price : null; // sale price if a sale is on, else base
  const isFree = applied && applied.discount_percent >= 100;
  const finalPrice = applied
    ? applied.final_price
    : effective;

  const apply = async () => {
    const code = coupon.trim();
    if (!code) return;
    setChecking(true);
    try {
      const r = await validateCoupon(code);
      if (!r.valid) {
        setApplied(null);
        toast.error(r.error || "That code isn't valid.");
      } else {
        setApplied({ discount_percent: r.discount_percent, code: r.code, final_price: r.final_price });
        toast.success(`Coupon applied — ${r.discount_percent}% off.`);
      }
    } catch {
      toast.error("Couldn't check that code. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const removeCoupon = () => {
    setApplied(null);
    setCoupon("");
  };

  const pay = async () => {
    setPaying(true);
    try {
      const res = await startCheckout(jobId, window.location.origin, applied?.code || null);
      if (res.unlocked) {
        toast.success("Unlocked — enjoy your full feedback!");
        onUnlocked();
        return;
      }
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setPaying(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="bg-white border border-clay/30 rounded-2xl p-8 shadow-[0_8px_32px_rgba(200,85,61,0.06)]"
      data-testid="paywall"
    >
      <div className="flex items-center gap-2 text-clay">
        <Lock className="h-5 w-5" strokeWidth={2} />
        <h2 className="font-heading text-xl text-ink">Unlock your full feedback</h2>
      </div>
      <p className="mt-2 text-ink-soft leading-relaxed max-w-2xl">
        You've seen your overall rating and full Question&nbsp;1 feedback. Unlock the
        rest for a one-off payment.
      </p>

      {pricing?.sale_active && (
        <div
          data-testid="sale-banner"
          className="mt-5 inline-flex items-center gap-2 rounded-xl bg-clay/[0.08] border border-clay/30 px-4 py-2.5 text-sm font-medium text-clay"
        >
          <Tag className="h-4 w-4" strokeWidth={2} />
          Sale on now — save {gbp(pricing.base_price - pricing.sale_price)}.
          {countdown && (
            <span className="inline-flex items-center gap-1 text-ink-soft" data-testid="sale-countdown">
              <Clock className="h-3.5 w-3.5" /> Ends in {countdown}
            </span>
          )}
        </div>
      )}

      <ul className="mt-5 space-y-2 text-sm text-ink-soft">
        {[
          ["Full feedback on Question 2", null],
          ["Full feedback on Question 3", null],
          ["Questions to push your thinking", Lightbulb],
          ["Download everything as a PDF", FileText],
        ].map(([label, Icon]) => (
          <li key={label} className="flex items-center gap-2">
            <Check className="h-4 w-4 text-forest shrink-0" strokeWidth={2.5} />
            {Icon ? <Icon className="h-4 w-4 text-ink-faint" strokeWidth={2} /> : null}
            {label}
          </li>
        ))}
      </ul>

      {/* Coupon */}
      <div className="mt-7">
        <label className="text-sm font-medium text-ink">Have a coupon code?</label>
        {applied ? (
          <div
            className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-forest/30 bg-forest/[0.06] px-4 py-3"
            data-testid="coupon-applied"
          >
            <span className="inline-flex items-center gap-2 text-sm text-forest font-medium">
              <Tag className="h-4 w-4" strokeWidth={2} />
              {applied.code} — {applied.discount_percent}% off
            </span>
            <button
              type="button"
              data-testid="coupon-remove"
              onClick={removeCoupon}
              className="text-ink-muted hover:text-ink"
              aria-label="Remove coupon"
            >
              <X className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        ) : (
          <div className="mt-2 flex gap-2">
            <Input
              data-testid="coupon-input"
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="Enter code"
              className="bg-white border-border focus-visible:ring-clay rounded-xl py-6 uppercase tracking-wide"
            />
            <Button
              type="button"
              data-testid="coupon-apply-button"
              onClick={apply}
              disabled={checking || !coupon.trim()}
              variant="outline"
              className="border-border text-ink hover:bg-sand-deep rounded-lg px-6 py-6"
            >
              {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
            </Button>
          </div>
        )}
      </div>

      {/* Price + pay */}
      <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-border pt-6">
        <div data-testid="paywall-price">
          {!pricing ? (
            <span className="font-heading text-2xl text-ink-faint">…</span>
          ) : isFree ? (
            <span className="font-heading text-2xl text-forest">Free with coupon</span>
          ) : applied ? (
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-2xl text-ink">{gbp(finalPrice)}</span>
              <span className="text-ink-faint line-through">{gbp(effective)}</span>
            </div>
          ) : pricing.sale_active ? (
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-2xl text-clay" data-testid="sale-price">{gbp(pricing.sale_price)}</span>
              <span className="text-ink-faint line-through" data-testid="base-price-struck">{gbp(pricing.base_price)}</span>
            </div>
          ) : (
            <span className="font-heading text-2xl text-ink">{gbp(pricing.base_price)}</span>
          )}
          <p className="text-sm text-ink-muted">One-off payment · unlocks this result</p>
        </div>
        <Button
          data-testid="pay-button"
          onClick={pay}
          disabled={paying || !pricing}
          className="bg-clay hover:bg-clay-hover text-white rounded-lg px-8 py-6 text-base font-medium"
        >
          {paying ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Please wait…
            </>
          ) : isFree ? (
            "Unlock for free"
          ) : (
            `Pay ${gbp(finalPrice ?? 0)} & unlock`
          )}
        </Button>
      </div>
      <p className="mt-4 text-xs text-ink-faint">
        Payments are processed securely by Stripe. Your card details are never seen or
        stored by us.
      </p>
    </motion.section>
  );
};
